// Multi-Section Timetable API operations using direct fetch

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type {
  MultiSectionTimetableRequest,
  MultiSectionTimetableResponse,
  TeacherSuggestionRequest,
  TeacherSuggestionsResponse,
  SchoolRulesRequest,
  SchoolRulesResponse,
  UpdateSchoolRulesRequest,
  TeacherClassAssociation,
  CreateTeacherClassAssociationRequest,
  TeacherClassAssociationsFilters,
  AutoFillRequest,
  AutoFillResponse,
  AutoFillAssignment,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// ============================================================================
// Multi-Section Timetable Endpoint
// ============================================================================

/**
 * Get multi-section timetable data for a class
 * Requirements: 1.1, 2.2, 7.1
 */
export async function getMultiSectionTimetable(
  req: MultiSectionTimetableRequest
): Promise<MultiSectionTimetableResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolContext = getSchoolContext()
    const date = req.date || new Date().toISOString().split('T')[0]

    // Fetch sections for the class
    const sectionsParams = new URLSearchParams()
    sectionsParams.append('select', 'id,class_id,section,class_teacher_id,class:classes!inner(id,class_name,school_id)')
    sectionsParams.append('class_id', `eq.${req.classId}`)

    const sectionsResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/class_sections?${sectionsParams}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    if (!sectionsResponse.ok) {
      const error = await sectionsResponse.json()
      throw new Error(error.message || 'Failed to fetch sections')
    }

    const sections = await sectionsResponse.json()

    if (!sections || sections.length === 0) {
      return { sections: [], records: [], conflicts: [] }
    }

    // Fetch timetable records for all sections
    const recordsParams = new URLSearchParams()
    recordsParams.append(
      'select',
      'id,school_id,academic_year,academic_year_id,valid_from,valid_to,day_of_week,period_id,teacher_id,subject_id,class_section_id,room_id,created_at,updated_at,is_supervision,supervision_location,supervision_notes,teacher:teachers(id,name,email,phone_number,teacher_type_id),subject:subjects(id,subject_name,subject_code,color_code),period:schedule_periods(id,period_number,time_start,time_end,is_break,break_type,period_name),room:rooms(id,name,description,capacity)'
    )
    recordsParams.append('school_id', `eq.${schoolContext}`)
    recordsParams.append('academic_year', `eq.${req.academicYear}`)
    recordsParams.append('class_section_id', `in.(${sections.map((s: any) => s.id).join(',')})`)
    // Filter for active records: valid_from <= date AND (valid_to IS NULL OR valid_to >= date)
    recordsParams.append('valid_from', `lte.${date}`)
    recordsParams.append('and', `(or(valid_to.is.null,valid_to.gte.${date}))`)

    const recordsResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?${recordsParams}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    if (!recordsResponse.ok) {
      const error = await recordsResponse.json()
      throw new Error(error.message || 'Failed to fetch timetable records')
    }

    const records = await recordsResponse.json()

    // Detect conflicts
    const conflicts = detectConflicts(records)

    return {
      sections,
      records,
      conflicts,
    }
  })
}

// ============================================================================
// Scoring Helper
// ============================================================================

/**
 * Compute a weighted suggestion score (0–100) for a teacher against a slot.
 *
 * Factors (mirrors priority_weights in school_rules):
 *   workloadBalance         — prefer teachers with fewer assigned periods
 *   teacherClassAssociation — strongly prefer teachers linked to this class
 *   subjectQualification    — all candidates already qualify (pre-filtered), full points
 *   subjectContinuity       — strongly prefer teachers already teaching this subject to this section
 *
 * Default weights are used when school_rules are unavailable.
 */
function computeTeacherScore(
  _teacherId: string,
  classId: string,
  currentPeriods: number,
  maxPeriods: number,
  associatedClassIds: Set<string>,
  alreadyTeachesSubjectInSection: boolean,
  weights: { workloadBalance: number; teacherClassAssociation: number; subjectQualification: number }
): { score: number; reasons: string[] } {
  const reasons: string[] = []

  // subject continuity: 100 if already teaching this subject to this section (highest priority)
  const continuityScore = alreadyTeachesSubjectInSection ? 100 : 0
  if (alreadyTeachesSubjectInSection) reasons.push('Already teaching this subject to this section')

  // workload: 100 when idle, 0 when at max
  const workloadScore = maxPeriods > 0 ? Math.max(0, 100 - (currentPeriods / maxPeriods) * 100) : 100
  if (currentPeriods === 0) reasons.push('No periods assigned yet')
  else reasons.push(`Workload: ${currentPeriods}/${maxPeriods} periods`)

  // association: 100 if linked to this class, 0 otherwise
  const isAssociated = associatedClassIds.has(classId)
  const associationScore = isAssociated ? 100 : 0
  if (isAssociated) reasons.push('Assigned to this class')

  // qualification: always 100 (pre-filtered by subject)
  const qualScore = 100
  reasons.push('Qualified for subject')

  // continuity weight is the same as teacherClassAssociation — strong signal
  const continuityWeight = weights.teacherClassAssociation
  const totalWeight = weights.workloadBalance + weights.teacherClassAssociation + weights.subjectQualification + continuityWeight
  if (totalWeight === 0) return { score: 50, reasons }

  const score = Math.round(
    (continuityScore * continuityWeight +
      workloadScore * weights.workloadBalance +
      associationScore * weights.teacherClassAssociation +
      qualScore * weights.subjectQualification) /
      totalWeight
  )

  return { score, reasons }
}

// ============================================================================
// Teacher Suggestions Endpoint
// ============================================================================

/**
 * Get intelligent teacher suggestions for a period slot.
 * Scores teachers using workload, class association, and subject qualification.
 * Requirements: 15.2, 15.3, 15.4, 15.5, 15.6, 15.7, 15.8, 15.9, 15.10
 */
export async function getTeacherSuggestions(
  req: TeacherSuggestionRequest
): Promise<TeacherSuggestionsResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolContext = getSchoolContext()
    if (!schoolContext) throw new Error('No school context')

    // Resolve classId from sectionId
    const sectionResp = await fetch(
      `${SUPABASE_URL}/rest/v1/class_sections?select=class_id&id=eq.${encodeURIComponent(req.sectionId)}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const sectionData = sectionResp.ok ? await sectionResp.json() : []
    const classId: string = sectionData[0]?.class_id ?? ''

    // Fetch teachers qualified for this subject (inner join on teacher_subjects)
    const select = 'id,name,email,phone_number,teacher_type_id,teacher_subjects!inner(subject_id),subjects:teacher_subjects(subject:subjects(id,subject_name,subject_code,color_code))'
    const teachersUrl =
      `${SUPABASE_URL}/rest/v1/teachers` +
      `?select=${encodeURIComponent(select)}` +
      `&school_id=eq.${encodeURIComponent(schoolContext)}` +
      `&is_active=eq.true` +
      `&teacher_subjects.subject_id=eq.${encodeURIComponent(req.subjectId)}`

    const teachersResponse = await fetch(teachersUrl, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })
    if (!teachersResponse.ok) {
      const error = await teachersResponse.json()
      throw new Error(error.message || 'Failed to fetch teachers')
    }
    const teachers: any[] = await teachersResponse.json()

    if (teachers.length === 0) return { suggestions: [] }

    const teacherIds = teachers.map((t) => t.id)

    // Fetch workload: count of timetable records per teacher for this school/year
    const workloadParams = new URLSearchParams()
    workloadParams.append('select', 'teacher_id')
    workloadParams.append('school_id', `eq.${schoolContext}`)
    workloadParams.append('teacher_id', `in.(${teacherIds.join(',')})`)
    const workloadResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?${workloadParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const workloadRecords: any[] = workloadResp.ok ? await workloadResp.json() : []
    const workloadMap = new Map<string, number>()
    for (const r of workloadRecords) {
      workloadMap.set(r.teacher_id, (workloadMap.get(r.teacher_id) || 0) + 1)
    }

    // Fetch teacher-class associations for this class
    const assocParams = new URLSearchParams()
    assocParams.append('select', 'teacher_id,class_id')
    assocParams.append('class_id', `eq.${classId}`)
    const assocResp = await fetch(
      `${SUPABASE_URL}/rest/v1/teacher_class_associations?${assocParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const assocData: any[] = assocResp.ok ? await assocResp.json() : []
    const associatedTeacherIds = new Set(assocData.map((a) => a.teacher_id))
    // Build a set of classIds associated per teacher (for scoring helper)
    const teacherAssociatedClasses = new Map<string, Set<string>>()
    for (const a of assocData) {
      if (!teacherAssociatedClasses.has(a.teacher_id)) teacherAssociatedClasses.set(a.teacher_id, new Set())
      teacherAssociatedClasses.get(a.teacher_id)!.add(a.class_id)
    }

    // Fetch existing active records for this section+subject to detect subject continuity
    const continuityParams = new URLSearchParams()
    continuityParams.append('select', 'teacher_id')
    continuityParams.append('class_section_id', `eq.${req.sectionId}`)
    continuityParams.append('subject_id', `eq.${req.subjectId}`)
    continuityParams.append('valid_to', 'is.null')
    const continuityResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?${continuityParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const continuityRecords: any[] = continuityResp.ok ? await continuityResp.json() : []
    const subjectContinuityTeachers = new Set(continuityRecords.map((r) => r.teacher_id))

    // Fetch school rules for priority weights (best-effort, fall back to defaults)
    const rulesParams = new URLSearchParams()
    rulesParams.append('select', 'priority_weights,teacher_rules')
    rulesParams.append('school_id', `eq.${schoolContext}`)
    const rulesResp = await fetch(
      `${SUPABASE_URL}/rest/v1/school_rules?${rulesParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/vnd.pgrst.object+json' } }
    )
    const rulesData = rulesResp.ok ? await rulesResp.json() : null
    const weights = {
      workloadBalance: rulesData?.priority_weights?.workloadBalance ?? 5,
      teacherClassAssociation: rulesData?.priority_weights?.teacherClassAssociation ?? 8,
      subjectQualification: rulesData?.priority_weights?.subjectQualification ?? 10,
    }
    const maxPeriods: number = rulesData?.teacher_rules?.maxPeriodsPerWeek ?? 40

    // Score and sort
    const suggestions = teachers
      .map((teacher: any) => {
        const currentPeriods = workloadMap.get(teacher.id) || 0
        const associatedClasses = teacherAssociatedClasses.get(teacher.id) ?? new Set<string>()
        const alreadyTeachesSubjectInSection = subjectContinuityTeachers.has(teacher.id)
        const { score, reasons } = computeTeacherScore(
          teacher.id, classId, currentPeriods, maxPeriods, associatedClasses, alreadyTeachesSubjectInSection, weights
        )
        return {
          teacher,
          score,
          rank: 0, // assigned after sort
          reasons,
          isAssociated: associatedTeacherIds.has(teacher.id),
          workload: {
            currentPeriods,
            maxPeriods,
            percentage: maxPeriods > 0 ? Math.round((currentPeriods / maxPeriods) * 100) : 0,
          },
          availability: 'available' as const,
          qualifications: (teacher.subjects || []).map((s: any) => s.subject).filter(Boolean),
        }
      })
      .sort((a, b) => b.score - a.score)
      .map((s, i) => ({ ...s, rank: i + 1 }))

    return { suggestions }
  })
}

// ============================================================================
// School Rules Endpoints
// ============================================================================

/**
 * Get school rules for a specific academic year
 * Requirements: 18.1, 18.9, 18.11
 */
export async function getSchoolRules(req: SchoolRulesRequest): Promise<SchoolRulesResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('school_id', `eq.${req.schoolId}`)
    params.append('academic_year', `eq.${req.academicYear}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/school_rules?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'School rules not found')
    }

    return await response.json()
  })
}

/**
 * Update school rules
 * Requirements: 18.1, 18.9, 18.11
 */
export async function updateSchoolRules(
  schoolId: string,
  academicYear: string,
  req: UpdateSchoolRulesRequest
): Promise<SchoolRulesResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/school_rules?school_id=eq.${schoolId}&academic_year=eq.${academicYear}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify(req),
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update school rules')
    }

    const data: SchoolRulesResponse[] = await response.json()
    if (!data[0]) throw new Error('School rules not found')

    return data[0]
  })
}

// ============================================================================
// Teacher Class Associations Endpoints
// ============================================================================

/**
 * Get teacher class associations
 * Requirements: 18.2
 */
export async function getTeacherClassAssociations(
  filters: TeacherClassAssociationsFilters = {}
): Promise<TeacherClassAssociation[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')

    if (filters.teacherId) {
      params.append('teacher_id', `eq.${filters.teacherId}`)
    }
    if (filters.classId) {
      params.append('class_id', `eq.${filters.classId}`)
    }
    if (filters.academicYear) {
      params.append('academic_year', `eq.${filters.academicYear}`)
    }

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/teacher_class_associations?${params}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch teacher class associations')
    }

    return await response.json()
  })
}

/**
 * Create teacher class association
 * Requirements: 18.2
 */
export async function createTeacherClassAssociation(
  req: CreateTeacherClassAssociationRequest
): Promise<TeacherClassAssociation> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teacher_class_associations`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(req),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create teacher class association')
    }

    const data: TeacherClassAssociation[] = await response.json()
    return data[0]
  })
}

/**
 * Delete a teacher class association by id
 * Requirements: 18.2
 */
export async function deleteTeacherClassAssociation(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/teacher_class_associations?id=eq.${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete teacher class association')
    }
  })
}

// ============================================================================
// Auto-Fill Endpoint
// ============================================================================

/**
 * Auto-fill empty slots with intelligent teacher suggestions
 * Requirements: 15.15, 15.16
 */
export async function autoFillTimetable(req: AutoFillRequest): Promise<AutoFillResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolContext = getSchoolContext()
    const days: string[] = req.day
      ? [req.day]
      : ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']

    // 1. Fetch the class to get its class_schedule_id
    const classResp = await fetch(
      `${SUPABASE_URL}/rest/v1/classes?id=eq.${req.classId}&select=id,class_schedule_id`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    if (!classResp.ok) throw new Error('Failed to fetch class')
    const classRows: any[] = await classResp.json()
    const classData = classRows[0]
    if (!classData?.class_schedule_id) {
      throw new Error('Class has no schedule assigned. Please assign a class schedule before using auto-fill.')
    }

    // 2. Fetch all non-break schedule_periods for this class's schedule (no day filter — day is on timetable_records)
    const periodsParams = new URLSearchParams()
    periodsParams.append('select', 'id,period_number,time_start,time_end,is_break')
    periodsParams.append('class_schedule_id', `eq.${classData.class_schedule_id}`)
    periodsParams.append('is_break', 'eq.false')
    const periodsResp = await fetch(`${SUPABASE_URL}/rest/v1/schedule_periods?${periodsParams}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })
    if (!periodsResp.ok) throw new Error('Failed to fetch schedule periods')
    const allPeriods: any[] = await periodsResp.json()

    // 3. Fetch ALL school timetable records (not just the requested sections) so that
    //    cross-class teacher conflicts are detected during availability checks.
    const recordsParams = new URLSearchParams()
    recordsParams.append('select', 'id,class_section_id,period_id,day_of_week,teacher_id,subject_id,period:schedule_periods(time_start,time_end)')
    recordsParams.append('school_id', `eq.${schoolContext}`)
    if (req.academicYearId) {
      recordsParams.append('academic_year_id', `eq.${req.academicYearId}`)
    } else {
      recordsParams.append('academic_year', `eq.${req.academicYear}`)
    }
    recordsParams.append('valid_from', `lte.${req.date}`)
    recordsParams.append('and', `(or(valid_to.is.null,valid_to.gte.${req.date}))`)
    const recordsResp = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${recordsParams}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })
    if (!recordsResp.ok) throw new Error('Failed to fetch timetable records')
    const allSchoolRecords: any[] = await recordsResp.json()

    // Records scoped to the requested sections (for slot-filling and subject balance logic)
    const existingRecords = allSchoolRecords.filter(r => req.sectionIds.includes(r.class_section_id))

    // Build a set of filled slots: "sectionId|periodId|day" (day in UPPER CASE)
    const filledSlots = new Set(
      existingRecords.map((r) => `${r.class_section_id}|${r.period_id}|${r.day_of_week}`)
    )

    // Count subject usage per section to balance allocation
    const sectionSubjectCount = new Map<string, Map<string, number>>()
    for (const r of existingRecords) {
      if (!sectionSubjectCount.has(r.class_section_id)) {
        sectionSubjectCount.set(r.class_section_id, new Map())
      }
      const subMap = sectionSubjectCount.get(r.class_section_id)!
      subMap.set(r.subject_id, (subMap.get(r.subject_id) || 0) + 1)
    }

    // 4. Fetch all active teachers with their subjects for this school
    const teachersParams = new URLSearchParams()
    teachersParams.append('select', 'id,name,teacher_subjects(subject_id)')
    teachersParams.append('school_id', `eq.${schoolContext}`)
    teachersParams.append('is_active', 'eq.true')
    const teachersResp = await fetch(`${SUPABASE_URL}/rest/v1/teachers?${teachersParams}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })
    if (!teachersResp.ok) throw new Error('Failed to fetch teachers')
    const teachers: any[] = await teachersResp.json()

    // Build teacher workload map: teacherId -> number of periods already assigned this week (school-wide)
    const teacherWorkload = new Map<string, number>()
    for (const r of allSchoolRecords) {
      teacherWorkload.set(r.teacher_id, (teacherWorkload.get(r.teacher_id) || 0) + 1)
    }

    // Build teacher->subjects map
    const teacherSubjects = new Map<string, string[]>()
    for (const t of teachers) {
      const subjectIds = (t.teacher_subjects || []).map((ts: any) => ts.subject_id)
      teacherSubjects.set(t.id, subjectIds)
    }

    // Collect all unique subjects: use class_subjects associations (same logic as getSubjectsForClass).
    // If the class has explicit subject-class associations, restrict to those subjects only.
    // If no associations exist (unrestricted class), fall back to all subjects any teacher can teach.
    const classSubjectAssocResp = await fetch(
      `${SUPABASE_URL}/rest/v1/class_subjects?class_id=eq.${encodeURIComponent(req.classId)}&select=subject_id`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const classSubjectAssocs: any[] = classSubjectAssocResp.ok ? await classSubjectAssocResp.json() : []

    const allTeacherSubjectIds = new Set<string>()
    for (const subIds of teacherSubjects.values()) {
      for (const sid of subIds) allTeacherSubjectIds.add(sid)
    }
    // Restricted path: class has explicit subject associations → use only those.
    // Unrestricted path: no associations defined → fall back to all teachable subjects.
    const classSubjectIds = classSubjectAssocs.length > 0
      ? new Set(classSubjectAssocs.map((a: any) => a.subject_id))
      : allTeacherSubjectIds

    // 4b. Fetch teacher-class associations for the requested class
    const assocParams = new URLSearchParams()
    assocParams.append('select', 'teacher_id,class_id')
    assocParams.append('class_id', `eq.${req.classId}`)
    const assocResp = await fetch(
      `${SUPABASE_URL}/rest/v1/teacher_class_associations?${assocParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    const assocData: any[] = assocResp.ok ? await assocResp.json() : []
    const associatedTeacherIds = new Set(assocData.map((a: any) => a.teacher_id))

    // 4c. Fetch school rules for priority weights (best-effort)
    const rulesParams = new URLSearchParams()
    rulesParams.append('select', 'priority_weights,teacher_rules')
    rulesParams.append('school_id', `eq.${schoolContext}`)
    const rulesResp = await fetch(
      `${SUPABASE_URL}/rest/v1/school_rules?${rulesParams}`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/vnd.pgrst.object+json' } }
    )
    const rulesData = rulesResp.ok ? await rulesResp.json() : null
    const fillWeights = {
      workloadBalance: rulesData?.priority_weights?.workloadBalance ?? 5,
      teacherClassAssociation: rulesData?.priority_weights?.teacherClassAssociation ?? 8,
      subjectQualification: rulesData?.priority_weights?.subjectQualification ?? 10,
    }
    const maxPeriodsPerWeek: number = rulesData?.teacher_rules?.maxPeriodsPerWeek ?? 40

    // 5. Find empty slots and assign teachers
    const assignments: AutoFillAssignment[] = []
    const unassigned: AutoFillResponse['unassigned'] = []

    for (const sectionId of req.sectionIds) {
      for (const day of days) {
        // schedule_periods have no day column — iterate all non-break periods for each day
        for (const period of allPeriods) {
          const slotKey = `${sectionId}|${period.id}|${day}`
          if (filledSlots.has(slotKey)) continue // already filled

          // Check time-overlap availability for this teacher against ALL school records (cross-class)
          const isTeacherBusy = (teacherId: string) => {
            return allSchoolRecords.some((r) => {
              if (r.teacher_id !== teacherId || r.day_of_week !== day) return false
              if (!r.period?.time_start || !r.period?.time_end) return false
              return r.period.time_start < period.time_end && r.period.time_end > period.time_start
            })
          }

          const isNewAssignmentBusy = (teacherId: string) => {
            // Check slots assigned in this auto-fill run
            for (const a of assignments) {
              if (a.teacherId !== teacherId || a.day !== day) continue
              const ap = allPeriods.find(p => p.id === a.periodId)
              if (!ap) continue
              if (ap.time_start < period.time_end && ap.time_end > period.time_start) return true
            }
            return false
          }

          // Determine which subject to assign: pick the least-used subject for this section
          const subMap = sectionSubjectCount.get(sectionId) || new Map<string, number>()
          const candidateSubjects = classSubjectIds.size > 0
            ? Array.from(classSubjectIds)
            : []

          if (candidateSubjects.length === 0) {
            unassigned.push({ sectionId, periodId: period.id, day, reason: 'No subjects found for this class' })
            continue
          }

          // Sort subjects by usage count ascending (fill least-assigned subjects first)
          const sortedSubjects = candidateSubjects.sort(
            (a, b) => (subMap.get(a) || 0) - (subMap.get(b) || 0)
          )

          let assigned = false
          for (const subjectId of sortedSubjects) {
            // Find available teachers for this subject, scored by weighted algorithm
            // Build continuity set for this section+subject from existing records
            const sectionSubjectContinuityTeachers = new Set(
              existingRecords
                .filter((r) => r.class_section_id === sectionId && r.subject_id === subjectId)
                .map((r) => r.teacher_id)
            )
            const availableTeachers = teachers
              .filter((t) => {
                const subs = teacherSubjects.get(t.id) || []
                if (!subs.includes(subjectId)) return false
                if (isNewAssignmentBusy(t.id)) return false
                if (isTeacherBusy(t.id)) return false
                return true
              })
              .map((t) => {
                const currentPeriods = teacherWorkload.get(t.id) || 0
                const associatedClasses = associatedTeacherIds.has(t.id)
                  ? new Set([req.classId])
                  : new Set<string>()
                const alreadyTeachesSubjectInSection = sectionSubjectContinuityTeachers.has(t.id)
                const { score, reasons } = computeTeacherScore(
                  t.id, req.classId, currentPeriods, maxPeriodsPerWeek, associatedClasses, alreadyTeachesSubjectInSection, fillWeights
                )
                return { teacher: t, score, reasons }
              })
              .sort((a, b) => b.score - a.score)

            if (availableTeachers.length === 0) continue

            // Prefer teachers associated with this class; only fall back to unassociated
            // teachers if no associated teacher is available for this subject+slot.
            const associatedAvailable = availableTeachers.filter(t => associatedTeacherIds.has(t.teacher.id))
            const candidatePool = associatedAvailable.length > 0 ? associatedAvailable : availableTeachers

            const { teacher, score, reasons } = candidatePool[0]
            const isAssociated = associatedTeacherIds.has(teacher.id)
            assignments.push({
              sectionId,
              periodId: period.id,
              day,
              teacherId: teacher.id,
              subjectId,
              score,
              reason: isAssociated
                ? `Assigned to this class — ${reasons.find(r => r.startsWith('Workload')) ?? ''}`
                : (reasons.find(r => r.startsWith('Workload')) ?? 'Best available teacher'),
            })

            // Mark slot as taken
            filledSlots.add(slotKey)
            teacherWorkload.set(teacher.id, (teacherWorkload.get(teacher.id) || 0) + 1)
            subMap.set(subjectId, (subMap.get(subjectId) || 0) + 1)
            sectionSubjectCount.set(sectionId, subMap)
            assigned = true
            break
          }

          if (!assigned) {
            unassigned.push({ sectionId, periodId: period.id, day, reason: 'No available teacher found' })
          }
        }
      }
    }

    // 5. Bulk insert the assignments as timetable records
    if (assignments.length > 0) {
      const recordsToCreate = assignments.map((a) => ({
        school_id: schoolContext,
        teacher_id: a.teacherId,
        subject_id: a.subjectId,
        class_section_id: a.sectionId,
        period_id: a.periodId,
        academic_year: req.academicYear,
        academic_year_id: req.academicYearId ?? null,
        valid_from: req.date,
        valid_to: null,
        day_of_week: a.day,
      }))

      const createResp = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify(recordsToCreate),
      })

      if (!createResp.ok) {
        const error = await createResp.json()
        throw new Error(error.message || 'Failed to create auto-fill records')
      }
    }

    return { assignments, unassigned }
  })
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Detect conflicts in timetable records using time-overlap comparison.
 * After the redesign, different classes use different class_schedules so
 * period_id values are not comparable across schedules.
 */
function detectConflicts(records: any[]): any[] {
  const conflicts: any[] = []

  // Helper: do two time windows overlap?
  function timesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
    return startA < endB && startB < endA
  }

  // Group records by day_of_week
  const byDay = new Map<string, any[]>()
  for (const record of records) {
    const day = record.day_of_week
    if (!byDay.has(day)) byDay.set(day, [])
    byDay.get(day)!.push(record)
  }

  for (const [_day, dayRecords] of byDay.entries()) {
    // Check teacher conflicts: same teacher, overlapping time windows
    const teacherMap = new Map<string, any[]>()
    for (const record of dayRecords) {
      if (!record.teacher_id) continue
      if (!teacherMap.has(record.teacher_id)) teacherMap.set(record.teacher_id, [])
      teacherMap.get(record.teacher_id)!.push(record)
    }

    for (const [_teacherId, teacherRecords] of teacherMap.entries()) {
      for (let i = 0; i < teacherRecords.length; i++) {
        for (let j = i + 1; j < teacherRecords.length; j++) {
          const a = teacherRecords[i]
          const b = teacherRecords[j]
          const aStart = a.period?.time_start
          const aEnd = a.period?.time_end
          const bStart = b.period?.time_start
          const bEnd = b.period?.time_end
          if (aStart && aEnd && bStart && bEnd && timesOverlap(aStart, aEnd, bStart, bEnd)) {
            conflicts.push({
              type: 'teacher',
              severity: 'hard',
              message: `Teacher ${a.teacher?.name ?? _teacherId} is assigned to multiple classes at overlapping times`,
              affectedResources: [a.id, b.id],
            })
          }
        }
      }
    }

    // Check room conflicts: same room, overlapping time windows
    const roomMap = new Map<string, any[]>()
    for (const record of dayRecords) {
      if (!record.room_id) continue
      if (!roomMap.has(record.room_id)) roomMap.set(record.room_id, [])
      roomMap.get(record.room_id)!.push(record)
    }

    for (const [roomId, roomRecords] of roomMap.entries()) {
      for (let i = 0; i < roomRecords.length; i++) {
        for (let j = i + 1; j < roomRecords.length; j++) {
          const a = roomRecords[i]
          const b = roomRecords[j]
          const aStart = a.period?.time_start
          const aEnd = a.period?.time_end
          const bStart = b.period?.time_start
          const bEnd = b.period?.time_end
          if (aStart && aEnd && bStart && bEnd && timesOverlap(aStart, aEnd, bStart, bEnd)) {
            conflicts.push({
              type: 'room',
              severity: 'hard',
              message: `Room ${roomRecords[0].room?.name ?? roomId} is assigned to multiple classes at overlapping times`,
              affectedResources: [a.id, b.id],
            })
          }
        }
      }
    }
  }

  return conflicts
}
